import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { UnitDto } from 'api/units/models/UnitDto';
import { createLine } from 'store/budget/reducer';
import Utils from 'common/services/Utils';
import { ImportRow, ColumnMappingEntry, BudgetImportField } from './types';

function getCellValue (row: ImportRow, mappings: ColumnMappingEntry[], field: BudgetImportField): string {
    const fieldMappings = mappings.filter(m => m.budgetField === field);
    if (fieldMappings.length === 0) return '';
    if (fieldMappings.length === 1) return row.rawCells[fieldMappings[0].excelColumnIndex] ?? '';
    return fieldMappings
        .map(m => (row.rawCells[m.excelColumnIndex] ?? '').trim())
        .filter(v => v)
        .join(' ');
}

function findParentSection (numberStr: string, sectionsByNumber: Map<string, string>): string | null {
    if (!numberStr) return null;
    const segments = numberStr.split('.');
    for (let i = segments.length - 1; i >= 1; i--) {
        const parentNumber = segments.slice(0, i).join('.');
        const parentKey = sectionsByNumber.get(parentNumber);
        if (parentKey) return parentKey;
    }
    return null;
}

export function buildBudgetLines (
    importRows: ImportRow[],
    existingLines: BudgetLineDto[],
    units: UnitDto[],
    mappings: ColumnMappingEntry[]
): BudgetLineDto[] {
    const newLines: BudgetLineDto[] = [];
    const sectionsByNumber = new Map<string, string>();
    let currentSectionKey: string | null = null;
    let lastSectionWasNumbered = false;
    let lastSectionParentKey: string | null = null;

    for (const row of importRows) {
        if (!row.type) continue;
        const type = row.type;

        if (type === 'S') {
            const numberStr = getCellValue(row, mappings, 'number').trim();
            let parentKey: string | null;
            if (numberStr) {
                parentKey = findParentSection(numberStr, sectionsByNumber);
            } else if (lastSectionWasNumbered) {
                parentKey = currentSectionKey;
            } else {
                parentKey = lastSectionParentKey;
            }

            const line = createLine(BudgetLineType.SECTION, parentKey, Utils.newGuid());
            line.designation = getCellValue(row, mappings, 'designation');
            line.isOpen = true;

            const qtyStr = getCellValue(row, mappings, 'quantity');
            if (qtyStr) {
                const parsed = parseFloat(qtyStr.replace(',', '.'));
                if (!isNaN(parsed)) {
                    line.quantity = parsed;
                }
            }

            const unitStr = getCellValue(row, mappings, 'unit');
            if (unitStr) {
                const matchedUnit = units.find(
                    u => u.symbol.toLowerCase() === unitStr.toLowerCase() ||
                         u.name.toLowerCase() === unitStr.toLowerCase()
                );
                if (matchedUnit) {
                    line.unitId = matchedUnit.id;
                    line.unitSymbol = matchedUnit.symbol;
                }
            }

            line.numberOverride = numberStr || '';

            if (numberStr) {
                sectionsByNumber.set(numberStr, line.key);
            }

            newLines.push(line);
            currentSectionKey = line.key;
            lastSectionWasNumbered = !!numberStr;
            lastSectionParentKey = parentKey;
        } else {
            const parentKey = currentSectionKey;
            const line = createLine(BudgetLineType.NOTE, parentKey, Utils.newGuid());
            line.designation = getCellValue(row, mappings, 'designation');

            newLines.push(line);
        }
    }

    return [...existingLines, ...newLines];
}
